import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { StageKey } from '../../office/unit/lifecycle';
import { chainOf, layoutFlow } from './flowLayout';
import { actionInfo, type WfStep } from './workflowModel';

// One edge colour per lifecycle stage, so a card shows where it belongs at a glance.
const STAGE_TONE: Record<StageKey, string> = {
  booking: 'oklch(0.62 0.12 250)',
  dp: 'oklch(0.66 0.15 50)',
  agreement: 'oklch(0.58 0.13 300)',
  loan: 'oklch(0.6 0.12 200)',
  instalments: 'oklch(0.68 0.12 95)',
  deed: 'oklch(0.55 0.13 20)',
  possession: 'oklch(0.58 0.12 150)',
};

type Line = { d: string; tone: 'up' | 'down' | 'base' | 'fade' };

/** Workflow as a top-to-bottom chart: each step under the steps it waits for, with arrows between them. */
export function FlowChart(props: {
  steps: WfStep[];
  stages: StageKey[];
  selected: number;
  onSelect: (index: number) => void;
  otherLabel: (key: string) => string;
}) {
  const { steps, stages, selected } = props;
  const layout = useMemo(() => layoutFlow(steps), [steps]);
  const selId = steps[selected] ? `s${selected}` : '';
  const chain = useMemo(() => (selId ? chainOf(layout.edges, selId) : { up: new Set<string>(), down: new Set<string>() }), [layout.edges, selId]);
  const host = useRef<HTMLDivElement>(null);
  const outer = useRef<HTMLDivElement>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [size, setSize] = useState({ w: 0, h: 0 });
  // Zoom: the chart is laid out at full size and scaled; the sizer keeps the scroll area matching the scaled size.
  const [zoom, setZoom] = useState(1);
  const [frame, setFrame] = useState(0);
  const clampZoom = (z: number) => Math.min(1.5, Math.max(0.3, Math.round(z * 100) / 100));
  const fit = () => {
    const o = outer.current;
    if (!o || !size.w || !size.h) return;
    const availH = Math.max(240, window.innerHeight - o.getBoundingClientRect().top - 32);
    setZoom(clampZoom(Math.min(1, (o.clientWidth - 12) / size.w, availH / size.h)));
  };

  // Draw the arrows from where the cards actually ended up.
  useLayoutEffect(() => {
    const el = host.current;
    if (!el) return;
    const draw = () => {
      const box = el.getBoundingClientRect();
      const z = zoom || 1;
      const at = (id: string) => {
        const r = el.querySelector<HTMLElement>(`[data-node="${CSS.escape(id)}"]`)?.getBoundingClientRect();
        return r ? { left: (r.left - box.left) / z, top: (r.top - box.top) / z, width: r.width / z, height: r.height / z, bottom: (r.bottom - box.top) / z } : null;
      };
      const out: Line[] = [];
      layout.edges.forEach((e) => {
        const a = at(e.from);
        const b = at(e.to);
        if (!a || !b) return;
        const x1 = a.left + a.width / 2;
        const y1 = a.bottom;
        const x2 = b.left + b.width / 2;
        const y2 = b.top - 4;
        const bend = Math.max(24, (y2 - y1) / 2);
        const onChain = selId && (e.to === selId || chain.up.has(e.to)) && (e.from === selId || chain.up.has(e.from));
        const below = selId && (e.from === selId || chain.down.has(e.from)) && (e.to === selId || chain.down.has(e.to));
        out.push({ d: `M${x1},${y1} C${x1},${y1 + bend} ${x2},${y2 - bend} ${x2},${y2}`, tone: onChain ? 'up' : below ? 'down' : selId ? 'fade' : 'base' });
      });
      // Highlighted arrows last, so they sit on top.
      out.sort((p, q) => (p.tone === 'fade' || p.tone === 'base' ? 0 : 1) - (q.tone === 'fade' || q.tone === 'base' ? 0 : 1));
      setLines(out);
      setSize({ w: el.offsetWidth, h: el.offsetHeight });
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(el);
    if (outer.current) ro.observe(outer.current);
    return () => ro.disconnect();
  }, [chain, layout, selId, zoom, frame]);

  // Ctrl/⌘ + scroll (or a trackpad pinch) zooms the chart.
  useEffect(() => {
    const o = outer.current;
    if (!o) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      setZoom((z) => clampZoom(z * (e.deltaY < 0 ? 1.08 : 0.92)));
    };
    o.addEventListener('wheel', onWheel, { passive: false });
    return () => o.removeEventListener('wheel', onWheel);
  }, []);

  const toneOf = (id: string) => (!selId ? '' : id === selId ? ' sel' : chain.up.has(id) ? ' up' : chain.down.has(id) ? ' down' : ' fade');

  return (
    <div className="owf-wrap">
    <div className="owf-zoom" role="group" aria-label="Zoom the chart">
      <button className="ou-ibtn" onClick={() => setZoom((z) => clampZoom(z - 0.1))} aria-label="Zoom out" title="Zoom out" disabled={zoom <= 0.3}>
        <svg className="ot-ic" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M5 12h14" /></svg>
      </button>
      <button className="owf-pct num" onClick={() => setZoom(1)} title="Back to 100%">{Math.round(zoom * 100)}%</button>
      <button className="ou-ibtn" onClick={() => setZoom((z) => clampZoom(z + 0.1))} aria-label="Zoom in" title="Zoom in" disabled={zoom >= 1.5}>
        <svg className="ot-ic" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
      </button>
      <button className="ot-btn sm" onClick={() => { fit(); setFrame((f) => f + 1); }} title="Fit the whole flow in view">Fit</button>
      <span className="faint owf-tip">Ctrl or ⌘ + scroll to zoom</span>
    </div>
    <div className="owf" ref={outer}>
    <div className="owf-sizer" style={{ width: size.w * zoom || undefined, height: size.h * zoom || undefined }}>
    <div className="owf-canvas" ref={host} style={{ transform: `scale(${zoom})`, minWidth: outer.current ? outer.current.clientWidth / zoom - 12 : undefined }}>
      <svg className="owf-lines" width={size.w} height={size.h} aria-hidden="true">
        <defs>
          {(['base', 'up', 'down', 'fade'] as const).map((t) => (
            <marker key={t} id={`owf-arrow-${t}`} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M0,0 L10,5 L0,10 z" className={`owf-head ${t}`} />
            </marker>
          ))}
        </defs>
        {lines.map((l, i) => <path key={i} d={l.d} className={`owf-line ${l.tone}`} markerEnd={`url(#owf-arrow-${l.tone})`} />)}
      </svg>
      {layout.rows.map((row, r) => (
        <div className="owf-row" key={r}>
          {row.map((n) =>
            n.kind === 'gate' ? (
              <div key={n.id} className={`owf-gate${toneOf(n.id)}`} data-node={n.id} title="Checked automatically">
                {n.label}
              </div>
            ) : n.kind === 'answer' ? (
              <button
                key={n.id}
                type="button"
                className={`owf-answer ${n.branch}${toneOf(n.id)}`}
                data-node={n.id}
                onClick={() => props.onSelect(n.questionIndex)}
                aria-label={`${n.branch === 'yes' ? 'Yes' : 'No'} answer to ${steps[n.questionIndex].title}`}
              >
                <strong>{n.branch === 'yes' ? 'Yes' : 'No'}</strong>
                <span>{n.branch === 'yes' ? 'Continue fit-out' : 'Skip fit-out'}</span>
              </button>
            ) : (
              <button
                key={n.id}
                className={`owf-card${toneOf(n.id)}${steps[n.index].enabled === false ? ' off' : ''}`}
                data-node={n.id}
                style={{ ['--tone' as any]: STAGE_TONE[stages[n.index]] }}
                onClick={() => props.onSelect(n.index)}
                aria-current={n.index === selected}
              >
                <span className="top">
                  <span className="num">{n.index + 1}</span>
                  <span>{actionInfo(steps[n.index].actionType).short}</span>
                  {steps[n.index].dependsMode === 'any' && steps[n.index].dependsOnSteps.length > 1 ? <span className="any" title="Ready when any of its steps is done">ANY</span> : null}
                </span>
                <span className="t">{steps[n.index].title || 'Untitled step'}</span>
                {steps[n.index].dependsOnSteps.filter((d) => d.includes(':')).map((d) => (
                  <span key={d} className="ext">{props.otherLabel(d)}</span>
                ))}
              </button>
            )
          )}
        </div>
      ))}
      {layout.looped.length ? (
        <div className="owf-loop" role="alert">
          <b>These steps wait for each other, so they can never become ready:</b>
          <div className="owf-row">
            {layout.looped.map((i) => (
              <button key={i} className="owf-card bad" onClick={() => props.onSelect(i)}>
                <span className="top"><span className="num">{i + 1}</span></span>
                <span className="t">{steps[i].title}</span>
              </button>
            ))}
          </div>
          <span>Open one and remove the step it should not wait for.</span>
        </div>
      ) : null}
      {!steps.length ? <p className="faint">No steps yet.</p> : null}
    </div>
    </div>
    </div>
    </div>
  );
}
